import React, { useState, useEffect } from 'react';
import './Leaderboard.css';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

function Leaderboard({ onClose }) {
  const [selectedMode, setSelectedMode] = useState('3x3');
  const [selectedDifficulty, setSelectedDifficulty] = useState('easy');
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchScores = async () => {
    setLoading(true);
    setError(null);
    try {
      const scoresRef = collection(db, 'scores');
      
      // Try the filtered query first (requires composite index)
      try {
        const q = query(
          scoresRef,
          where('gridSize', '==', selectedMode),
          where('difficulty', '==', selectedDifficulty),
          orderBy('score', 'desc'),
          limit(10)
        );

        const querySnapshot = await getDocs(q);
        const fetchedScores = [];
        querySnapshot.forEach((doc) => {
          fetchedScores.push({ id: doc.id, ...doc.data() });
        });

        setScores(fetchedScores);
      } catch (indexError) {
        // If index error, fall back to client-side filtering
        console.warn('Composite index not found. Using fallback query.', indexError);
        
        const allScoresQuery = query(scoresRef, limit(100));
        const querySnapshot = await getDocs(allScoresQuery);
        const allScores = [];
        querySnapshot.forEach((doc) => {
          allScores.push({ id: doc.id, ...doc.data() });
        });

        // Filter and sort on client side
        const filteredScores = allScores
          .filter(score => score.gridSize === selectedMode && score.difficulty === selectedDifficulty)
          .sort((a, b) => b.score - a.score)
          .slice(0, 10);

        setScores(filteredScores);
        
        if (indexError.code === 'failed-precondition') {
          setError('Index creation needed. Check console for link to create it.');
          console.error('Create the composite index here:', indexError.message);
        }
      }
    } catch (error) {
      console.error('Error fetching scores:', error);
      setError(`Failed to load scores: ${error.message}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMode, selectedDifficulty]);

  return (
    <div className="leaderboard-overlay">
      <div className="leaderboard-container">
        <button className="close-button" onClick={onClose}>×</button>
        
        <h2>🏆 Leaderboard</h2>

        <div className="filter-section">
          <div className="filter-group">
            <label>Mode:</label>
            <div className="button-group">
              <button
                className={selectedMode === '3x3' ? 'active' : ''}
                onClick={() => setSelectedMode('3x3')}
              >
                3x3
              </button>
              <button
                className={selectedMode === '4x4' ? 'active' : ''}
                onClick={() => setSelectedMode('4x4')}
              >
                4x4
              </button>
            </div>
          </div>

          <div className="filter-group">
            <label>Difficulty:</label>
            <div className="button-group">
              <button
                className={selectedDifficulty === 'easy' ? 'active' : ''}
                onClick={() => setSelectedDifficulty('easy')}
              >
                Easy
              </button>
              <button
                className={selectedDifficulty === 'medium' ? 'active' : ''}
                onClick={() => setSelectedDifficulty('medium')}
              >
                Medium
              </button>
              <button
                className={selectedDifficulty === 'hard' ? 'active' : ''}
                onClick={() => setSelectedDifficulty('hard')}
              >
                Hard
              </button>
            </div>
          </div>
        </div>

        <div className="scores-list">
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={fetchScores} className="retry-button">Retry</button>
            </div>
          )}
          {loading ? (
            <div className="loading">Loading...</div>
          ) : !error && scores.length === 0 ? (
            <div className="no-scores">No scores yet for this mode. Be the first!</div>
          ) : !error && (
            <table>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Player</th>
                  <th>Score</th>
                  <th>Rounds</th>
                </tr>
              </thead>
              <tbody>
                {scores.map((score, index) => (
                  <tr key={score.id} className={index < 3 ? `top-${index + 1}` : ''}>
                    <td className="rank">
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                    </td>
                    <td className="player">{score.playerName}</td>
                    <td className="score">{score.score}</td>
                    <td className="rounds">{score.round}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
